import type { LegacyPoint } from '../model/schema';

/**
 * Yandex geocoding helpers, shared by the address search box and the
 * click-to-pick map.
 *
 * Both directions go through the same `geocoderSearch` call (see
 * `../api/geocoder-api`): forward search takes a street name, reverse takes a
 * `"long, lat"` string. That is how the old panel did it too (`searchYmaps`
 * in the old `utils/`), which is why the reverse path passes coordinates as
 * TEXT rather than a separate parameter.
 *
 */

/** Tashkent centre + bounds, transcribed from the old `searchYmaps`. */
export const TASHKENT_CENTER: [number, number] = [69.240562, 41.311081];
export const TASHKENT_BOUNDS: [[number, number], [number, number]] = [
  [69.1158, 41.1551],
  [69.4086, 41.4234],
];

/**
 * One geocoding hit, narrowed from the SDK's dynamically typed response.
 *
 * The optional trio comes only from the SUGGEST service (`suggestAddresses`),
 * which is what the old panel's typing suggestions actually used: `tag` names
 * the venue-category icon, `subtitle` is the grey second line, and `distance`
 * a right-aligned human string ("450м"/"3км"). Geocoder hits carry none.
 */
export interface LegacyGeoResult {
  address: string;
  point: LegacyPoint;
  tag?: string;
  subtitle?: string;
  distance?: string;
}

/** `[long, lat]` → the `"long, lat"` text the reverse lookup expects. */
export function pointToSearchText(coordinates: [number, number]): string {
  return `${coordinates[0]}, ${coordinates[1]}`;
}

/**
 * Narrows a Geocoder HTTP API (1.x) JSON payload defensively — it is an
 * external service, so nothing about the shape is trusted. A hit with no
 * coordinate is dropped (an address we cannot place is useless to an order),
 * and the label joins `name` and `description` the way the old panel did
 * ("улица Лутфи, 21А, Ташкент"), because `name` alone is often just a street
 * with no locality.
 *
 * `joiner` is the old map's own inconsistency, preserved: a CLICKED point
 * joined with `", "` (`MapSelect.tsx`), a DRAGGED pin with `" "`
 * (`handlePlacemarkDragEnd`). Both spellings land in the form's address
 * field, so the difference is user-visible.
 */
export function narrowGeocoderResults(
  payload: unknown,
  joiner = ', ',
): LegacyGeoResult[] {
  const envelope = payload as {
    response?: { GeoObjectCollection?: { featureMember?: unknown } };
  } | null;
  const members = envelope?.response?.GeoObjectCollection?.featureMember;
  const list = Array.isArray(members) ? members : [];

  return list.flatMap((item): LegacyGeoResult[] => {
    const geoObject = (item as { GeoObject?: unknown } | null)?.GeoObject as {
      name?: string;
      description?: string;
      Point?: { pos?: string };
    } | null;
    if (!geoObject) return [];

    // `pos` is a "long lat" string, space-separated.
    const [long, lat] = (geoObject.Point?.pos ?? '').split(' ').map(Number);
    if (
      long === undefined ||
      lat === undefined ||
      Number.isNaN(long) ||
      Number.isNaN(lat)
    ) {
      return [];
    }

    const name = geoObject.name ?? '';
    const description = geoObject.description ?? '';
    const address = [name, description].filter(Boolean).join(joiner);
    if (!address) return [];

    return [{ address, point: { long, lat } }];
  });
}

/**
 * Whether a `[long, lat]` pair sits inside the Tashkent window — the old
 * `searchYmaps` dropped suggest hits outside its `cameraBounds` the same way.
 */
export function isWithinTashkent(long: number, lat: number): boolean {
  const [[west, south], [east, north]] = TASHKENT_BOUNDS;
  return long >= west && long <= east && lat >= south && lat <= north;
}

/**
 * null means address text; an empty array means coordinates outside our area.
 * Keep both supported interpretations rather than guessing their axis order.
 */
export function coordinateSearchPoints(text: string): LegacyPoint[] | null {
  const number = '[+-]?(?:\\d+(?:\\.\\d*)?|\\.\\d+)';
  const match = new RegExp(`^\\s*(${number})\\s*,\\s*(${number})\\s*$`).exec(text);
  if (!match) return null;
  const first = Number(match[1]);
  const second = Number(match[2]);
  return [
    { lat: first, long: second },
    ...(first === second ? [] : [{ lat: second, long: first }]),
  ].filter((point) => isWithinTashkent(point.long, point.lat));
}

/**
 * Narrows a `suggest-maps.yandex.uz/suggest-geo` payload — the endpoint behind
 * the old panel's typing suggestions (`searchYmaps`'s non-SDK branch). Rows
 * out of the Tashkent window or without a coordinate are dropped, and the
 * courier-facing `distance` is rendered exactly as the old rows did: metres
 * under a kilometre, whole kilometres above.
 */
export function narrowSuggestResults(payload: unknown): LegacyGeoResult[] {
  const envelope = payload as { results?: unknown } | null;
  const rows = Array.isArray(envelope?.results) ? envelope.results : [];

  return rows.flatMap((row): LegacyGeoResult[] => {
    const item = row as {
      pos?: string;
      text?: string;
      title?: { text?: string };
      subtitle?: { text?: string };
      tags?: string[];
      distance?: { value?: number };
    } | null;
    if (!item?.text) return [];

    const [long, lat] = (item.pos ?? '').split(',').map(Number);
    if (
      long === undefined ||
      lat === undefined ||
      Number.isNaN(long) ||
      Number.isNaN(lat) ||
      !isWithinTashkent(long, lat)
    ) {
      return [];
    }

    const address = item.title?.text ?? item.text;
    if (!address) return [];

    const metres = item.distance?.value;
    const distance =
      typeof metres === 'number'
        ? metres / 1000 < 1
          ? `${metres.toFixed(0)}м`
          : `${(metres / 1000).toFixed(0)}км`
        : undefined;

    return [
      {
        address,
        point: { long, lat },
        ...(item.tags?.[0] !== undefined ? { tag: item.tags[0] } : {}),
        ...(item.subtitle?.text !== undefined ? { subtitle: item.subtitle.text } : {}),
        ...(distance !== undefined ? { distance } : {}),
      },
    ];
  });
}

/**
 * Corner bounds → the `ll` (window centre) + `spn` (window size) pair the
 * Geocoder takes instead of a bounds box. Paired with `rspn=1` it restricts
 * results to the window, which is what `strictBounds` did on the SDK call.
 */
export function boundsToSearchWindow(bounds: [[number, number], [number, number]]): {
  ll: string;
  spn: string;
} {
  const [[west, south], [east, north]] = bounds;
  // 6 decimal places (~10cm) is plenty for a city window and keeps binary
  // float noise (69.5 - 69.1 = 0.400…0057) out of the URL.
  const deg = (value: number) => Number(value.toFixed(6));
  return {
    ll: `${deg((west + east) / 2)},${deg((south + north) / 2)}`,
    spn: `${deg(east - west)},${deg(north - south)}`,
  };
}
